/**
 * 「未登録のBB ID」(D-100)。CSV取込で **オペレーターマスタに無かった BB ID** を氏名つきで残しておき、
 * オペレーターマスタの画面から **ボタン1つで仮登録**(時給は未設定の印つき)できるようにする。
 *
 * Firestore: `unknown_bb_ids/{BB ID}`。admin だけが読み書きする(氏名しか入らない。金額・個人情報は持たない)。
 * 仮登録するか「今はしない」を押すと消える。
 */
import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { DateStr } from '@core/lib/types';

export type UnknownBbId = {
  bb_id: string;
  /** CSVに出ていた氏名(分からなければ空) */
  name: string;
  /** 最初に見つかった日 / 最後に見つかった日(取込対象の日付) */
  first_date: DateStr;
  last_date: DateStr;
  /** 見つかった取込の回数 */
  seen: number;
  /** 「登録しない」にした(D-207)。取り込み直しても一覧に出さない(取込は merge で書くのでこの印は残る) */
  ignored?: boolean;
};

const COL = 'unknown_bb_ids';

export async function fetchUnknownBbIds(): Promise<UnknownBbId[]> {
  const snap = await getDocs(collection(getDb(), COL));
  return snap.docs.map((d) => ({ ...(d.data() as UnknownBbId), bb_id: d.id })).sort((a, b) => a.bb_id.localeCompare(b.bb_id));
}

/** 取込で見つかった未登録の BB ID を残す(同じ BB ID は日付と回数だけ更新する) */
export async function recordUnknownBbIds(list: { bb_id: string; name: string }[], date: DateStr): Promise<void> {
  if (list.length === 0) return;
  const known = new Map((await fetchUnknownBbIds()).map((u) => [u.bb_id, u]));
  await Promise.all(
    list.map((u) => {
      const prev = known.get(u.bb_id);
      return setDoc(
        doc(getDb(), COL, u.bb_id),
        {
          name: u.name || (prev?.name ?? ''),
          first_date: prev && prev.first_date < date ? prev.first_date : date,
          last_date: prev && prev.last_date > date ? prev.last_date : date,
          seen: (prev?.seen ?? 0) + 1,
          updated_at: serverTimestamp(),
        },
        { merge: true },
      );
    }),
  );
}

export async function removeUnknownBbId(bbId: string): Promise<void> {
  await deleteDoc(doc(getDb(), COL, bbId));
}

/** 「登録しない」(D-207)。消さずに印を付ける(次の取込でまた出てこないように) */
export async function ignoreUnknownBbId(bbId: string): Promise<void> {
  await setDoc(doc(getDb(), COL, bbId), { ignored: true, updated_at: serverTimestamp() }, { merge: true });
}

/** BB ID から 出勤形態・報酬区分・架電内容 を推す(採番ルール。合っているかは登録のときに直せる) */
export function guessFromBbId(bbId: string, name = ''): { work_style: 'shinagawa' | 'zaitaku' | 'kanri' | 'timee'; pay_type: 'hourly' | 'commission'; business: 'kyutoki' | 'fuyohin' } {
  const id = bbId.toUpperCase();
  const white = /W$/.test(name.trim()) || /W$/.test(id);
  const business = white ? 'fuyohin' : 'kyutoki';
  // 管理者の昔のID(MGR01・OSM02 など。2026-10-12 未登録のBB IDで確認)も管理として扱う
  if (id.startsWith('SNM') || id.startsWith('MGR') || id.startsWith('OSM')) return { work_style: 'kanri', pay_type: 'hourly', business };
  if (id.startsWith('STM')) return { work_style: 'timee', pay_type: 'hourly', business };
  if (id.startsWith('SN')) return { work_style: 'shinagawa', pay_type: 'hourly', business };
  if (id.startsWith('RS')) return { work_style: 'zaitaku', pay_type: 'commission', business };
  return { work_style: 'zaitaku', pay_type: 'hourly', business };
}
